import { useEffect, useState } from "react";

import { notifySessionExpired, requireMfaStepUp } from "../features/auth/mfaStepUp";
import "../styles/platform-branding.css";

export type PlatformBrandingState = {
  brand_mark_configured: boolean;
  brand_mark_url: string | null;
  version: number | null;
  updated_at: string | null;
};

const DEFAULT_STATE: PlatformBrandingState = {
  brand_mark_configured: false,
  brand_mark_url: null,
  version: null,
  updated_at: null,
};

const OFFICIAL_KAVOOSH_MARK = "/kavoosh-logo-official.png";
const BRAND_FAVICON_SELECTOR = 'link[data-kavoosh-platform-brand="favicon"]';
type Listener = (state: PlatformBrandingState) => void;
const listeners = new Set<Listener>();
let cached: PlatformBrandingState | null = null;
let loading: Promise<PlatformBrandingState> | null = null;

function cookie(name: string): string | undefined {
  return document.cookie.split(";").map((item) => item.trim())
    .find((item) => item.startsWith(`${name}=`))?.slice(name.length + 1);
}

function parseBranding(input: unknown): PlatformBrandingState {
  if (!input || typeof input !== "object") return DEFAULT_STATE;
  const value = input as Record<string, unknown>;
  const configured = value.brand_mark_configured === true;
  const url = typeof value.brand_mark_url === "string" && value.brand_mark_url.startsWith("/api/v1/branding/brand-mark")
    ? value.brand_mark_url : null;
  return {
    brand_mark_configured: configured && Boolean(url),
    brand_mark_url: configured ? url : null,
    version: typeof value.version === "number" && Number.isInteger(value.version) ? value.version : null,
    updated_at: typeof value.updated_at === "string" ? value.updated_at : null,
  };
}

function emit(state: PlatformBrandingState) {
  cached = state;
  listeners.forEach((listener) => listener(state));
}

export function subscribePlatformBranding(listener: Listener): () => void {
  listeners.add(listener);
  if (cached) listener(cached);
  return () => listeners.delete(listener);
}

export async function loadPlatformBranding(force = false): Promise<PlatformBrandingState> {
  if (!force && cached) return cached;
  if (!force && loading) return loading;
  loading = fetch("/api/v1/branding", {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  }).then(async (response) => {
    if (!response.ok) return DEFAULT_STATE;
    return parseBranding(await response.json().catch(() => null));
  }).catch(() => DEFAULT_STATE).then((state) => {
    emit(state);
    return state;
  }).finally(() => { loading = null; });
  return loading;
}

async function adminBrandingMutation(method: "POST" | "DELETE", file?: File, allowStepUpRetry = true): Promise<PlatformBrandingState> {
  const csrf = cookie("kavoosh_csrf");
  const response = await fetch("/api/v1/admin/branding/brand-mark", {
    method,
    credentials: "include",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(csrf ? { "X-CSRF-Token": decodeURIComponent(csrf) } : {}),
      ...(file ? { "Content-Type": file.type } : {}),
    },
    ...(file ? { body: file } : {}),
  });
  const body = await response.json().catch(() => ({})) as { detail?: unknown } & Record<string, unknown>;
  if (!response.ok) {
    const detail = body.detail;
    const code = typeof detail === "object" && detail !== null && "code" in detail
      && typeof detail.code === "string" ? detail.code : null;
    if (response.status === 401 && code === "AUTH_SESSION_EXPIRED") {
      notifySessionExpired();
      throw new Error("Your session is no longer available.");
    }
    if (response.status === 403 && code === "MFA_STEP_UP_REQUIRED" && allowStepUpRetry) {
      await requireMfaStepUp();
      return adminBrandingMutation(method, file, false);
    }
    if (typeof detail === "string") throw new Error(detail);
    if (typeof detail === "object" && detail !== null && "message" in detail && typeof detail.message === "string") {
      throw new Error(detail.message);
    }
    throw new Error(response.status >= 500
      ? "Platform branding could not be updated. No branding change was applied."
      : "Platform branding could not be updated.");
  }
  const state = parseBranding(body);
  emit(state);
  return state;
}

export const uploadPlatformBrandMark = (file: File) => adminBrandingMutation("POST", file);
export const removePlatformBrandMark = () => adminBrandingMutation("DELETE");

export function usePlatformBranding() {
  const [state, setState] = useState<PlatformBrandingState>(cached ?? DEFAULT_STATE);
  useEffect(() => {
    const unsubscribe = subscribePlatformBranding(setState);
    void loadPlatformBranding();
    return unsubscribe;
  }, []);
  return state;
}

function customBrandUrl(state: PlatformBrandingState): string | null {
  if (!state.brand_mark_configured || !state.brand_mark_url) return null;
  const separator = state.brand_mark_url.includes("?") ? "&" : "?";
  const cacheKey = state.version ?? Date.now();
  return `${state.brand_mark_url}${separator}cache=${encodeURIComponent(String(cacheKey))}`;
}

function ensureFavicon(type: string, href: string): HTMLLinkElement {
  let favicon = document.head.querySelector<HTMLLinkElement>(BRAND_FAVICON_SELECTOR);
  if (!favicon) {
    favicon = document.createElement("link");
    favicon.rel = "icon";
    favicon.dataset.kavooshPlatformBrand = "favicon";
    document.head.appendChild(favicon);
  }
  favicon.type = type;
  favicon.href = href;
  return favicon;
}

function applyBranding(state: PlatformBrandingState) {
  const root = document.documentElement;
  const url = customBrandUrl(state);
  if (url) {
    root.dataset.platformBrand = "custom";
    root.style.setProperty("--kavoosh-brand-mark-image", `url(${JSON.stringify(url)})`);
    ensureFavicon("image/webp", url);
  } else {
    root.dataset.platformBrand = "official";
    root.style.setProperty("--kavoosh-brand-mark-image", `url(${JSON.stringify(OFFICIAL_KAVOOSH_MARK)})`);
    ensureFavicon("image/png", OFFICIAL_KAVOOSH_MARK);
  }
}

export function GlobalPlatformBranding() {
  const state = usePlatformBranding();
  useEffect(() => applyBranding(state), [state]);
  return null;
}

export function PlatformBrandMarkPreview({ className = "" }: { className?: string }) {
  const state = usePlatformBranding();
  return <span className={`platform-brand-preview ${state.brand_mark_configured ? "is-custom" : "is-default"} ${className}`.trim()} aria-hidden="true">
    {state.brand_mark_configured && state.brand_mark_url
      ? <img src={`${state.brand_mark_url}${state.brand_mark_url.includes("?") ? "&" : "?"}preview=${state.version ?? 0}`} alt="" />
      : <img src={OFFICIAL_KAVOOSH_MARK} alt="" />}
  </span>;
}
